import { useEffect, useState } from 'preact/hooks';

// R1a — the ONE responsive system the whole shell reads from. Three sizes:
//   phone   (<768)   — the unchanged single column + bottom nav + strip.
//   tablet  (768–1119) — first target for the left side rail (iPad).
//   desktop (>=1120) — the same rail, wider centred content.
// Kept as a plain innerWidth read (not matchMedia) so a test can force a
// size by setting window.innerWidth and dispatching 'resize' — see
// Nav.test.tsx. The CSS layer mirrors these same numbers (global.css), but
// the JS breakpoint is the source of truth for WHICH nav component renders,
// since bottom-nav and side-rail are different DOM, not one styled two ways.
export type Breakpoint = 'phone' | 'tablet' | 'desktop';

export const TABLET_MIN = 768;
export const DESKTOP_MIN = 1120;

export function breakpointForWidth(width: number): Breakpoint {
  if (width >= DESKTOP_MIN) return 'desktop';
  if (width >= TABLET_MIN) return 'tablet';
  return 'phone';
}

/** True for tablet + desktop — the sizes that promote the bottom nav to the
 *  left side rail and move the strip into the content header. */
export function isRail(bp: Breakpoint): boolean {
  return bp !== 'phone';
}

export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(() =>
    breakpointForWidth(typeof window !== 'undefined' ? window.innerWidth : 0),
  );

  useEffect(() => {
    function onResize() {
      setBp(breakpointForWidth(window.innerWidth));
    }
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return bp;
}
